import { useState, useEffect, useRef } from "react";
import { Download, X, Share, Plus, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isAndroid = () => /Android/i.test(navigator.userAgent);

const isInStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as any).standalone === true;

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Swipe to dismiss
  const bannerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isDragging = useRef(false);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    currentX.current = 0;
    isDragging.current = true;
    if (bannerRef.current) bannerRef.current.style.transition = "none";
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    currentX.current = e.touches[0].clientX - startX.current;
    if (bannerRef.current) {
      bannerRef.current.style.transform = `translateX(${currentX.current}px)`;
      bannerRef.current.style.opacity = `${Math.max(0, 1 - Math.abs(currentX.current) / 200)}`;
    }
  };

  const onTouchEnd = () => {
    isDragging.current = false;
    if (Math.abs(currentX.current) > 100) {
      const dir = currentX.current > 0 ? 1 : -1;
      if (bannerRef.current) {
        bannerRef.current.style.transition = "transform 0.3s ease, opacity 0.3s ease";
        bannerRef.current.style.transform = `translateX(${dir * 400}px)`;
        bannerRef.current.style.opacity = "0";
      }
      setTimeout(handleDismiss, 300);
    } else {
      if (bannerRef.current) {
        bannerRef.current.style.transition = "transform 0.3s ease, opacity 0.3s ease";
        bannerRef.current.style.transform = "translateX(0)";
        bannerRef.current.style.opacity = "1";
      }
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem("pwa-dismissed")) {
      setDismissed(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (isIOS() && !isInStandaloneMode()) {
      const timer = setTimeout(() => setShowIOSPrompt(true), 2000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    if (isAndroid() && !isInStandaloneMode()) {
      const timer = setTimeout(() => setShowAndroidPrompt(true), 2000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (isAndroid()) {
      // Download APK on Android
      const link = document.createElement("a");
      link.href = "/SR_Software.apk";
      link.download = "SR_Software.apk";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      handleDismiss();
      return;
    }
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("pwa-dismissed", "true");
  };

  if (dismissed || isInStandaloneMode()) return null;
  if (!deferredPrompt && !showIOSPrompt && !showAndroidPrompt) return null;

  return (
    <>
      {/* Main banner - swipeable */}
      <div
        ref={bannerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="fixed bottom-20 left-4 right-4 z-50 mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-500"
      >
        <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-card p-4 shadow-lg backdrop-blur-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Download className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">Instalar o App</p>
            <p className="text-xs text-muted-foreground">Acesse mais rápido · deslize para fechar</p>
          </div>
          {deferredPrompt || showAndroidPrompt ? (
            <Button size="sm" onClick={handleInstall} className="shrink-0">
              Instalar
            </Button>
          ) : showIOSPrompt ? (
            <Button size="sm" onClick={() => setShowIOSSteps(true)} className="shrink-0">
              Como instalar
            </Button>
          ) : null}
          <button
            onClick={handleDismiss}
            className="shrink-0 rounded-full p-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* iOS step-by-step overlay */}
      {showIOSSteps && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowIOSSteps(false)}>
          <div
            className="w-full max-w-md rounded-t-2xl border border-border bg-card p-6 pb-10 animate-in slide-in-from-bottom duration-400"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground">Instalar o App no iPhone</h3>
              <button onClick={() => setShowIOSSteps(false)} className="rounded-full p-1 text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5">
              {/* Step 1 */}
              <div className="flex items-start gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  1
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">
                    Toque no botão <strong>Compartilhar</strong>
                  </p>
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 border border-border">
                    <Share className="h-4 w-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Na barra inferior do Safari</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowDown className="h-4 w-4 text-muted-foreground" />
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  2
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">
                    Toque em <strong>"Adicionar à Tela de Início"</strong>
                  </p>
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 border border-border">
                    <Plus className="h-4 w-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Role para baixo se necessário</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowDown className="h-4 w-4 text-muted-foreground" />
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  3
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">
                    Toque em <strong>"Adicionar"</strong>
                  </p>
                  <p className="text-xs text-muted-foreground">Pronto! O app aparecerá na sua tela inicial 🎉</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
